import { useTranslation } from 'react-i18next'

import { useDateLocale, useTimeZone } from '../../../../../hooks'
import { getEntityActionTypeSelectOptionsEditOnly } from '../../../../Entity'
import {
  getUserEditTextFieldFormModalInitialValues,
  getUserProfileHeaderInfoItemActions,
  getUserRoleSelectStaffOptions,
  getUserStaffEditFunctionalRoleFormModalInitialValues,
  USER_FORM_EXTERNAL_ID_FIELD_MAX_LENGTH,
  UserEditRoleFormModal,
  UserEditTextFieldFormModal,
  UserStaffEditFunctionalRoleFormModal,
  useUserProfileContext,
  useUserProfileHeaderEditSubmit,
  useUserProfileHeaderInfoItemsProps,
} from '../../../../User'

import {
  USER_PROFILE_PAGE_HEADER_STAFF_EDIT_EXTERNAL_ID_FIELD_NAME,
  USER_PROFILE_PAGE_HEADER_STAFF_EDIT_EXTERNAL_ID_MODAL_QA_NAME,
  USER_PROFILE_PAGE_HEADER_STAFF_EDIT_JOB_POSITION_FIELD_NAME,
  USER_PROFILE_PAGE_HEADER_STAFF_EDIT_JOB_POSITION_MODAL_QA_NAME,
  USER_PROFILE_PAGE_HEADER_STAFF_EDIT_SSO_ID_FIELD_NAME,
  USER_PROFILE_PAGE_HEADER_STAFF_EDIT_SSO_ID_MODAL_QA_NAME,
  USER_PROFILE_PAGE_HEADER_STAFF_INFO_ITEM_ACTION_QA_NAMES,
  USER_PROFILE_PAGE_HEADER_STAFF_INFO_ITEMS_LOADING_ITEMS_LENGTH,
  USER_PROFILE_PAGE_HEADER_STAFF_INFO_ITEMS_MAX_ITEMS_IN_ROW,
  USER_PROFILE_PAGE_HEADER_STAFF_TEXT_FIELD_MAX_LENGTH,
  UserUserProfilePageHeaderStaffInfoItemName,
} from './useUserProfilePageHeaderStaffInfoItems.constants'
import {
  getUserProfilePageHeaderStaffEditRoleInitialValues,
  getUserProfilePageHeaderStaffInfoItems,
} from './useUserProfilePageHeaderStaffInfoItems.utils'

export function useUserProfilePageHeaderStaffInfoItems() {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const {
    handleCloseModal,
    setModal,
    userData: staffData,
  } = useUserProfileContext()

  const { handleEditSubmit } = useUserProfileHeaderEditSubmit()

  const infoItems = getUserProfilePageHeaderStaffInfoItems({
    locale,
    staffData,
    t,
    timezone,
  })

  const editOnlyTypeSelectProps = {
    disabled: true,
    options: getEntityActionTypeSelectOptionsEditOnly(),
  }

  function handleOpenEditRoleModal() {
    setModal(
      <UserEditRoleFormModal
        formFieldsProps={{
          roleSelectOptions: getUserRoleSelectStaffOptions(t),
          typeSelectProps: editOnlyTypeSelectProps,
        }}
        formTitle='Modal.ChangeRoleTitleKey'
        initialValues={getUserProfilePageHeaderStaffEditRoleInitialValues({
          role: staffData?.role,
          t,
        })}
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  function handleOpenEditJobPositionModal() {
    setModal(
      <UserEditTextFieldFormModal
        fieldRequired
        fieldInfoKey='UserProfile.UserTitleTooltipKey'
        fieldLabelKey='UserProfile.UserTitleLabelKey'
        fieldMaxLength={USER_PROFILE_PAGE_HEADER_STAFF_TEXT_FIELD_MAX_LENGTH}
        fieldName={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_JOB_POSITION_FIELD_NAME}
        fieldPlaceholderKey='UserProfile.UserTitlePlaceholderKey'
        formFieldsProps={{
          withActionTypeSelect: false,
          withRequiredIcon: false,
        }}
        formTitle='UserProfile.EditUserTitleModalTitleKey'
        initialValues={getUserEditTextFieldFormModalInitialValues({
          fieldName:
            USER_PROFILE_PAGE_HEADER_STAFF_EDIT_JOB_POSITION_FIELD_NAME,
          value: staffData?.companyPostName,
          withActionType: false,
        })}
        key={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_JOB_POSITION_FIELD_NAME}
        qaName={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_JOB_POSITION_MODAL_QA_NAME}
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  function handleOpenEditFunctionalRoleModal() {
    setModal(
      <UserStaffEditFunctionalRoleFormModal
        formFieldsProps={{ typeSelectProps: editOnlyTypeSelectProps }}
        initialValues={getUserStaffEditFunctionalRoleFormModalInitialValues(
          staffData?.functionalRole
        )}
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  function handleOpenEditExternalIdModal() {
    setModal(
      <UserEditTextFieldFormModal
        fieldLabelKey='User.ExternalIdLabelKey'
        fieldMaxLength={USER_FORM_EXTERNAL_ID_FIELD_MAX_LENGTH}
        fieldName={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_EXTERNAL_ID_FIELD_NAME}
        fieldPlaceholderKey='User.ExternalIdPlaceholderKey'
        formFieldsProps={{ typeSelectProps: editOnlyTypeSelectProps }}
        formTitle='UserProfile.EditExternalIdModalTitleKey'
        initialValues={getUserEditTextFieldFormModalInitialValues({
          fieldName: USER_PROFILE_PAGE_HEADER_STAFF_EDIT_EXTERNAL_ID_FIELD_NAME,
          value: staffData?.externalId,
        })}
        key={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_EXTERNAL_ID_FIELD_NAME}
        qaName={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_EXTERNAL_ID_MODAL_QA_NAME}
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  function handleOpenEditSsoIdModal() {
    setModal(
      <UserEditTextFieldFormModal
        fieldLabelKey='UserProfile.SsoIdLabelKey'
        fieldMaxLength={USER_PROFILE_PAGE_HEADER_STAFF_TEXT_FIELD_MAX_LENGTH}
        fieldName={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_SSO_ID_FIELD_NAME}
        fieldPlaceholderKey='UserProfile.SsoIdPlaceholderKey'
        formFieldsProps={{ typeSelectProps: editOnlyTypeSelectProps }}
        formTitle='UserProfile.EditSsoIdModalTitleKey'
        initialValues={getUserEditTextFieldFormModalInitialValues({
          fieldName: USER_PROFILE_PAGE_HEADER_STAFF_EDIT_SSO_ID_FIELD_NAME,
          value: staffData?.ssoId,
        })}
        key={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_SSO_ID_FIELD_NAME}
        qaName={USER_PROFILE_PAGE_HEADER_STAFF_EDIT_SSO_ID_MODAL_QA_NAME}
        onClose={handleCloseModal}
        onSubmit={handleEditSubmit}
      />
    )
  }

  const infoItemsHandlers = {
    [UserUserProfilePageHeaderStaffInfoItemName.ExternalId]:
      getUserProfileHeaderInfoItemActions(handleOpenEditExternalIdModal),
    [UserUserProfilePageHeaderStaffInfoItemName.FunctionalRole]:
      getUserProfileHeaderInfoItemActions(handleOpenEditFunctionalRoleModal),
    [UserUserProfilePageHeaderStaffInfoItemName.JobPosition]:
      getUserProfileHeaderInfoItemActions(handleOpenEditJobPositionModal),
    [UserUserProfilePageHeaderStaffInfoItemName.Role]:
      getUserProfileHeaderInfoItemActions(handleOpenEditRoleModal),
    [UserUserProfilePageHeaderStaffInfoItemName.SsoId]:
      getUserProfileHeaderInfoItemActions(handleOpenEditSsoIdModal),
  }

  return useUserProfileHeaderInfoItemsProps({
    actionQaNames: USER_PROFILE_PAGE_HEADER_STAFF_INFO_ITEM_ACTION_QA_NAMES,
    handlers: infoItemsHandlers,
    items: infoItems,
    loadingItemsLength:
      USER_PROFILE_PAGE_HEADER_STAFF_INFO_ITEMS_LOADING_ITEMS_LENGTH,
    maxItemsInRow: USER_PROFILE_PAGE_HEADER_STAFF_INFO_ITEMS_MAX_ITEMS_IN_ROW,
  })
}
